/**
 * Lease (manager) — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/lease.schemas.ts`
 * imports and re-exports these to register the routes, and `apps/landlord/src/types/lease.ts`
 * derives its `LeaseTenant` / `LeaseListItem` / `CreateLeaseInput` / `UpdateLeaseInput` types
 * from the same objects. See packages/api-types/README.md.
 *
 * Domain name `lease-core` (not `lease`) — this is the manager-portal lease surface,
 * distinct from the tenant-portal `apps/api/src/routes/tenant/lease.schemas.ts` (a separate
 * migration batch/domain).
 *
 * Covers:
 *   GET    /landlord/api/units/:unitId/leases
 *   POST   /landlord/api/units/:unitId/leases
 *   GET    /landlord/api/leases/:leaseId
 *   PATCH  /landlord/api/leases/:leaseId
 *   GET    /landlord/api/leases
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Lease state — mirrored as a plain Zod enum (not cross-imported from
// @resida/shared): this package's only runtime dependency is zod (see README),
// and @resida/shared is not among its declared dependencies. Keep in sync with
// LEASE_STATES in packages/shared/src/lease-states.ts.
// ---------------------------------------------------------------------------
export const LeaseStateSchema = z.enum(['scheduled', 'active', 'ending', 'ended', 'archived']);
// ---------------------------------------------------------------------------
// Tenant information within a lease response
// ---------------------------------------------------------------------------
export const LeaseTenantResponseSchema = z.object({
    id: z.uuid().describe('Tenant UUID'),
    firstName: z.string().describe('Tenant first name'),
    lastName: z.string().describe('Tenant last name'),
    email: z.email().describe('Tenant email address'),
    phone: z.string().nullable().describe('Tenant phone number (null if not provided)'),
});
// ---------------------------------------------------------------------------
// Complete lease details response
// ---------------------------------------------------------------------------
export const LeaseResponseSchema = z.object({
    id: z.uuid().describe('Lease UUID'),
    unitId: z.uuid().describe('UUID of the unit this lease is for'),
    source: z.enum(['wizard', 'manual']).describe('How this lease was created'),
    startDate: z.string().describe('Lease start date in ISO 8601 format'),
    endDate: z.string().nullable().describe('Lease end date in ISO 8601 format (null for ongoing)'),
    rentAmount: z.number().describe('Monthly rent amount in dollars'),
    securityDeposit: z.number().describe('Security deposit amount in dollars'),
    state: LeaseStateSchema.describe('Current lease state'),
    actualEndDate: z
        .string()
        .nullable()
        .describe('Actual end date when archived (null if not yet ended)'),
    archivedAt: z.string().nullable().describe('Timestamp when lease was archived'),
    createdAt: z.string().describe('Timestamp when lease was created'),
    updatedAt: z.string().describe('Timestamp when lease was last updated'),
    tenants: z.array(LeaseTenantResponseSchema).describe('Array of tenants on this lease'),
});
// ---------------------------------------------------------------------------
// Create lease — request body
// ---------------------------------------------------------------------------
export const CreateLeaseRequestSchema = z.object({
    startDate: z.iso.date().describe('Lease start date in YYYY-MM-DD format'),
    endDate: z
        .string()
        .date()
        .optional()
        .describe('Lease end date in YYYY-MM-DD format (optional for ongoing leases)'),
    rentAmount: z.number().int().positive().max(99999999.99).describe('Monthly rent amount in whole cents'),
    securityDeposit: z
        .number()
        .int()
        .min(0)
        .max(99999999.99)
        .describe('Security deposit amount in whole cents'),
    tenantIds: z
        .array(z.uuid())
        .min(1, 'At least one tenant required')
        .describe('Array of tenant UUIDs to add to the lease (minimum 1)'),
    acknowledgeWarnings: z
        .boolean()
        .optional()
        .default(false)
        .describe('Set to true to proceed with lease creation despite validation warnings'),
});
// ---------------------------------------------------------------------------
// Update lease — request body
//
// NOTE(api-types-922): the batch spec that drove this migration said "the server uses
// one schema for both create and update" and asked for `UpdateLeaseInput` to derive from
// `CreateLeaseRequestSchema`. That is not what the route wires up — grep of
// apps/api/src/routes/manager/lease.routes.ts shows PATCH /landlord/api/leases/:leaseId
// registers `UpdateLeaseRequestSchema` (this one), a distinct, all-optional, no-tenantIds
// schema. Migrating to CreateLeaseRequestSchema would have wrongly required `tenantIds`
// on every update payload and rejected `endDate: null`. Deviated from the assigned mapping
// to match the real wire contract instead; flagged in the batch report.
// ---------------------------------------------------------------------------
export const UpdateLeaseRequestSchema = z.object({
    startDate: z.iso.date().optional().describe('New lease start date in YYYY-MM-DD format'),
    endDate: z
        .string()
        .date()
        .nullable()
        .optional()
        .describe('New lease end date in YYYY-MM-DD format (null for ongoing)'),
    rentAmount: z
        .number()
        .int()
        .positive()
        .max(99999999.99)
        .optional()
        .describe('New monthly rent amount in whole cents'),
    securityDeposit: z
        .number()
        .int()
        .min(0)
        .max(99999999.99)
        .optional()
        .describe('New security deposit amount in whole cents'),
});
// ---------------------------------------------------------------------------
// Rent increase (rent-increase-rule-engine feature, lease-lifecycle — The Rent
// Increase Process) — appended directly into this file rather than a separate
// module: this package has no barrel/`export *`-of-siblings convention (that
// pattern lives in packages/api-types/src/payments/index.ts's 8-file domain,
// not here). Mirrors the `service` layer's response/input shapes field-for-field
// (apps/api/src/services/rent-increase/rent-increase.types.ts) — the wire
// contract both `apps/api` and `apps/landlord`/`apps/tenant` import.
//
// Covers:
//   POST   /landlord/api/leases/:leaseId/rent-increases
//   POST   /landlord/api/leases/:leaseId/rent-increases/preview
//   GET    /landlord/api/leases/:leaseId/rent-increases
//   GET    /landlord/api/rent-increases
//   GET    /landlord/api/rent-increases/candidates
//   GET    /landlord/api/rent-increases/:id
//   PATCH  /landlord/api/rent-increases/:id
//   POST   /landlord/api/rent-increases/:id/activate
//   DELETE /landlord/api/rent-increases/:id
//   POST   /landlord/api/rent-increases/:id/steps/:stepId/generate-document
//   POST   /landlord/api/rent-increases/:id/steps/:stepId/complete
//   POST   /landlord/api/rent-increases/:id/steps/:stepId/send-email
//   GET    /tenant/api/leases/:leaseId/rent-increases
//   POST   /tenant/api/autopay-reauthorizations/:id/confirm
// ---------------------------------------------------------------------------
export const RentIncreaseServiceModeSchema = z
    .enum(['MANUAL', 'AUTOMATIC_EMAIL'])
    .describe('How the notice is served: the landlord serves it (MANUAL) or the system emails it (AUTOMATIC_EMAIL)');
/**
 * Every editable field, all optional. PATCH uses this shape via `.partial()`; the initiate body
 * re-declares `serviceMode` with its MANUAL default. The default must NOT sit on the PATCH shape:
 * zod v4 applies a default inside an optional key, so an omitted `serviceMode` would silently
 * reset a draft's mode to MANUAL on every unrelated edit.
 */
const RentIncreaseEditableFieldsSchema = z.object({
    newRentAmountCents: z.number().int().positive().describe('New monthly rent amount in cents'),
    effectiveDate: z
        .string()
        .describe('Effective date in YYYY-MM-DD format — validated server-side as first-of-month (BR-LL-17)'),
    autoUpdateLease: z
        .boolean()
        .optional()
        .describe('Whether completion should automatically update Lease.rentAmountCents (default true)'),
    serviceMode: RentIncreaseServiceModeSchema.optional(),
    emailConsentAttested: z
        .boolean()
        .optional()
        .describe("BR-LL-41. true records the landlord's attestation that every tenant agreed in writing to electronic service; required to send by email where the jurisdiction requires consent"),
});
export const InitiateRentIncreaseBodySchema = RentIncreaseEditableFieldsSchema.extend({
    serviceMode: RentIncreaseServiceModeSchema.default('MANUAL'),
});
export const UpdateRentIncreaseBodySchema = RentIncreaseEditableFieldsSchema.partial();
// BR-LL-37 — read-only preview of the notice plan before anything is created.
export const RentIncreasePreviewBodySchema = RentIncreaseEditableFieldsSchema.pick({
    newRentAmountCents: true,
    effectiveDate: true,
    serviceMode: true,
});
export const CompleteStepBodySchema = z.object({
    serviceDate: z
        .string()
        .optional()
        .describe('Date the step was served, YYYY-MM-DD (defaults to today if omitted)'),
    serviceNotes: z.string().optional().describe('Optional free-text notes about how service was performed'),
    servicePhotoDocumentId: z.uuid().optional().describe('Optional Document UUID of a proof-of-service photo'),
});
export const RentIncreaseStepResponseSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    dueDate: z.string().describe('YYYY-MM-DD'),
    serviceMethods: z.array(z.string()),
    signingEnvelopeId: z.uuid().nullable(),
    generatedDocumentId: z.uuid().nullable(),
    status: z.enum(['PENDING', 'COMPLETED', 'OVERDUE', 'SKIPPED']),
    completedAt: z.string().nullable(),
    completedByManagerId: z.uuid().nullable(),
    serviceDate: z.string().nullable().describe('YYYY-MM-DD'),
    serviceNotes: z.string().nullable(),
    servicePhotoDocumentId: z.uuid().nullable(),
    servedLate: z.boolean(),
    emails: z
        .array(z.object({
        tenantId: z.uuid(),
        tenantName: z.string(),
        status: z.enum(['SCHEDULED', 'SENT', 'DELIVERED', 'BOUNCED', 'FAILED', 'SKIPPED_NO_EMAIL']),
        sentAt: z.string().nullable(),
        bouncedAt: z.string().nullable(),
    }))
        .describe('Per-tenant emailed-notice outcomes (BR-LL-35)'),
    documentKind: z.enum(['JURISDICTION', 'GENERIC']).nullable().describe('BR-LL-38; null while no document exists'),
    completedBy: z.enum(['MANAGER', 'SYSTEM']).nullable().describe('Who completed the step (BR-LL-26/35)'),
    reopenedAt: z.string().nullable().describe('BR-LL-36: when a bounce reopened a system-completed step'),
});
export const RentIncreaseWarningSchema = z.object({
    type: z.enum(['CAP_EXCEEDED', 'ELIGIBILITY_TOO_EARLY', 'STEP_DEADLINE_INFEASIBLE']),
    message: z.string(),
});
export const RentIncreaseContextSchema = z.object({
    property: z.object({ id: z.uuid(), name: z.string() }),
    unit: z.object({ id: z.uuid(), unitNumber: z.string().nullable() }),
    tenants: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export const RentIncreaseResponseSchema = z.object({
    id: z.uuid(),
    leaseId: z.uuid(),
    currentRentAmountCents: z.number().int(),
    newRentAmountCents: z.number().int(),
    effectiveDate: z.string().describe('YYYY-MM-DD'),
    autoUpdateLease: z.boolean(),
    serviceMode: RentIncreaseServiceModeSchema,
    emailConsentAttestedAt: z.string().nullable(),
    nextScheduledEmailAt: z
        .string()
        .nullable()
        .describe('YYYY-MM-DD the automatic email for the next open step goes out; null unless ACTIVE + AUTOMATIC_EMAIL, the step needs no execution, and it is PENDING with no email attempted yet'),
    context: RentIncreaseContextSchema.describe('BR-LL-39: property, unit and tenants for list display'),
    status: z.enum(['DRAFT', 'ACTIVE', 'COMPLETED', 'CANCELLED']),
    completedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    createdByManagerId: z.uuid(),
    createdAt: z.string(),
    updatedAt: z.string(),
    steps: z.array(RentIncreaseStepResponseSchema),
    warnings: z.array(RentIncreaseWarningSchema),
});
export const RentIncreasePreviewResponseSchema = z.object({
    jurisdiction: z.object({ id: z.string(), name: z.string() }),
    steps: z.array(z.object({
        noticeId: z.string(),
        label: z.string(),
        dueDate: z.string().describe('YYYY-MM-DD'),
        scheduledEmailAt: z
            .string()
            .nullable()
            .describe('YYYY-MM-DD the automatic email would go out; null unless AUTOMATIC_EMAIL, permitted, and the step needs no execution'),
        requiresExecution: z.boolean(),
    })),
    warnings: z.array(RentIncreaseWarningSchema),
    recipients: z.array(z.object({ tenantId: z.uuid(), name: z.string(), hasEmail: z.boolean() })),
    electronicServicePermitted: z.boolean(),
    consentAttestationRequired: z.boolean(),
});
// BR-LL-39 — a lease the landlord could start a rent increase on (informational eligibility).
export const RentIncreaseCandidateSchema = z.object({
    leaseId: z.uuid(),
    property: z.object({ id: z.uuid(), name: z.string() }),
    unit: z.object({ id: z.uuid(), unitNumber: z.string().nullable() }),
    tenants: z.array(z.object({ id: z.uuid(), name: z.string() })),
    currentRentAmountCents: z.number().int(),
    nextEligibleDate: z.string().nullable().describe('YYYY-MM-DD; informational only, null with no completed prior increase'),
    hasPriorIncrease: z.boolean(),
});
export const RentIncreaseCandidatesResponseSchema = z.object({
    candidates: z.array(RentIncreaseCandidateSchema),
});
export const AutopayReauthorizationResponseSchema = z.object({
    id: z.uuid(),
    rentIncreaseId: z.uuid(),
    paymentScheduleId: z.uuid(),
    oldAmountCents: z.number().int(),
    newAmountCents: z.number().int(),
    status: z.enum(['PENDING', 'REAUTHORIZED', 'SUPERSEDED']),
    requestedAt: z.string(),
    reauthorizedAt: z.string().nullable(),
});
//# sourceMappingURL=index.js.map